// src/components/shell/AppShell.jsx
import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import TopBar from './TopBar';
import CommandPalette from './CommandPalette';
import ShortcutsSheet from './ShortcutsSheet';
import VerifyEmailBanner from '../VerifyEmailBanner';
import Spinner from '../Spinner.mjs';
import { pageTitle } from './navigation';
import { matchShortcut } from './shortcuts';
import '../../css/AppShell.css';

/**
 * The chrome every signed-in route renders inside.
 *
 * Before this existed, only the file browser had a navbar; every other page
 * claimed the whole viewport and drew its own "Back to app" bar, so leaving the
 * library meant losing navigation, the workspace switcher and the theme toggle.
 *
 * The shell owns the viewport box. A page fills `.qd-shell__main` and picks how
 * it scrolls with `.qd-page` (grows, the shell scrolls) or `.qd-page--fill`
 * (pinned to the available height, scrolls its own panes).
 */
export default function AppShell() {
    const { pathname } = useLocation();
    const [overlay, setOverlay] = useState(null); // 'palette' | 'shortcuts'

    useEffect(() => {
        document.title = `${pageTitle(pathname)} · QDex`;
    }, [pathname]);

    const closeOverlay = useCallback(() => setOverlay(null), []);

    useEffect(() => {
        const onKeyDown = (e) => {
            // A dialog on screen owns the keyboard — including our own two.
            const dialogOpen = document.querySelector('[role="dialog"]') !== null;
            const shortcut = matchShortcut(e, { dialogOpen });
            if (!shortcut) return;

            e.preventDefault();
            if (shortcut === 'search') {
                document.getElementById('qd-global-search')?.focus();
            } else if (shortcut === 'palette') {
                setOverlay('palette');
            } else if (shortcut === 'shortcuts') {
                setOverlay('shortcuts');
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, []);

    const skipToContent = (e) => {
        // Focus rather than follow the fragment: a hash in the URL would be read
        // as a tab id by pages that deep-link through it (Settings).
        e.preventDefault();
        document.getElementById('qd-main')?.focus();
    };

    return (
        <div className="qd-shell">
            <a href="#qd-main" className="qd-skip-link" onClick={skipToContent}>Skip to content</a>
            <TopBar />
            <main id="qd-main" className="qd-shell__main" tabIndex={-1}>
                <VerifyEmailBanner />
                {/* A page chunk loads INSIDE the shell, so the chrome stays put
                    while it resolves instead of the whole screen blanking. */}
                <Suspense fallback={<div className="qd-page qd-page--fill qd-shell__pending"><Spinner /></div>}>
                    <Outlet />
                </Suspense>
            </main>

            {/* Modal already restores focus to whatever was focused before it
                opened, so Escape lands you back where you started. */}
            <CommandPalette open={overlay === 'palette'} onClose={closeOverlay} />
            <ShortcutsSheet open={overlay === 'shortcuts'} onClose={closeOverlay} />
        </div>
    );
}
